import { useId } from 'react';
import { InputChevron } from './InputChevron';
import { SparkIcon } from '../SparkIcon';
import { Label } from '../Label/Label';
import { TagGeneral } from '../Tag/TagGeneral';
import type { InputMultiSelectProps } from './InputMultiSelect.types';
import './shared/inputSelect.css';

export function InputMultiSelect({
  label = 'Label',
  placeholder = 'Input',
  value,
  onSelect: _onSelect,
  size = 'medium',
  fill,
  showLabel = true,
  showAsterisk = false,
  showHelpIcon = false,
  showPrefixIcon = false,
  tags = [],
  overflowCount = 0,
  disabled = false,
  open = false,
  visualState,
  showFocus,
  onToggle,
  onTagRemove,
  className = '',
}: InputMultiSelectProps) {
  const labelId = useId();
  const resolvedState = disabled ? 'disabled' : visualState ?? 'default';
  const isFocused = showFocus ?? resolvedState === 'pressed';
  const effectiveTags = value ?? tags;
  const resolvedFill = fill ?? (effectiveTags.length > 0 ? 'filled' : 'empty');
  const isFilled = resolvedFill === 'filled';
  const isInteractive = Boolean(onToggle) && !disabled;

  const rootClasses = [
    'spark-input-select',
    `spark-input-select-${size}`,
    `spark-input-select-fill-${resolvedFill}`,
    `spark-input-select-state-${resolvedState}`,
    disabled ? 'spark-input-select-disabled' : '',
    isFocused ? 'spark-input-select-show-focus' : '',
    isInteractive ? 'spark-input-select-interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleFieldClick = () => {
    if (isInteractive) {
      onToggle?.();
    }
  };

  return (
    <div className={rootClasses}>
      {showLabel ? (
        <Label
          id={labelId}
          label={label}
          showAsterisk={showAsterisk}
          showIcon={showHelpIcon}
        />
      ) : null}
      <div className="spark-input-select-field-wrap">
        <div
          className="spark-input-select-field"
          role="combobox"
          aria-labelledby={showLabel ? labelId : undefined}
          aria-disabled={disabled || undefined}
          aria-expanded={isInteractive ? open : undefined}
          tabIndex={isInteractive ? 0 : undefined}
          onClick={handleFieldClick}
          onKeyDown={(event) => {
            if (!isInteractive) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onToggle?.();
            }
          }}
        >
          {showPrefixIcon ? <SparkIcon slug="user" size="small" className="spark-input-select-prefix" /> : null}
          {isFilled ? (
            <div className="spark-input-select-tags">
              {effectiveTags.map((tag, index) => (
                <TagGeneral
                  key={`${tag}-${index}`}
                  label={tag}
                  variant={onTagRemove ? 'closeable' : 'default'}
                  onClose={onTagRemove ? () => onTagRemove(tag, index) : undefined}
                />
              ))}
              {overflowCount > 0 ? (
                <TagGeneral label={`+${overflowCount}`} variant="default" className="spark-input-select-tag-overflow" />
              ) : null}
            </div>
          ) : (
            <span className="spark-input-select-value">{placeholder}</span>
          )}
          <InputChevron open={open} disabled={disabled} className="spark-input-select-chevron" />
        </div>
      </div>
    </div>
  );
}
